import React from "react";

export type EmptyKind = "orders" | "recent" | "filters" | "notifications" | "help" | "error";

interface MerchantEmptyStateProps {
  kind: EmptyKind;
  title?: string;
  description?: string;
  actionLabel?: string;
  actionHref?: string;
  onAction?: () => void;
  layout?: "embed" | "page";
}

const COPY: Record<EmptyKind, { title: string; description: string }> = {
  orders: {
    title: "No deliveries yet",
    description: "Paid Shopify orders show here after Zulu imports them.",
  },
  recent: {
    title: "No recent deliveries",
    description: "New imported orders will appear in this list.",
  },
  filters: {
    title: "No matching orders",
    description: "Try another date, status, or search.",
  },
  notifications: {
    title: "You're all caught up",
    description: "Delivery updates for this store will show up here.",
  },
  help: {
    title: "Need a hand?",
    description: "Email Zulu support and we will help with this store.",
  },
  error: {
    title: "Could not load deliveries",
    description: "Check your connection and try again.",
  },
};

const MerchantEmptyState: React.FC<MerchantEmptyStateProps> = ({
  kind,
  title,
  description,
  actionLabel,
  actionHref,
  onAction,
  layout = "embed",
}) => {
  const copy = COPY[kind];
  const action = actionLabel && (onAction || actionHref) ? (
    actionHref ? (
      <a className="zulu-empty__action" href={actionHref}>
        {actionLabel}
      </a>
    ) : (
      <button type="button" className="zulu-empty__action" onClick={onAction}>
        {actionLabel}
      </button>
    )
  ) : null;

  return (
    <div
      className={`zulu-empty zulu-empty--${kind} zulu-empty--${layout}`}
      role={kind === "error" ? "alert" : "status"}
    >
      <span className="zulu-empty__icon" aria-hidden="true">
        {kind === "filters" ? <IconSearch /> : null}
        {kind === "error" ? <IconAlert /> : null}
        {kind === "notifications" ? <IconBell /> : null}
        {kind === "help" ? <IconHelp /> : null}
        {kind === "orders" || kind === "recent" ? <IconBox /> : null}
      </span>
      <h3>{title || copy.title}</h3>
      <p>{description || copy.description}</p>
      {action}
    </div>
  );
};

const IconBox = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M3 8l9-4 9 4-9 4-9-4z" />
    <path d="M3 8v8l9 4 9-4V8" />
    <path d="M12 12v8" />
  </svg>
);

const IconSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="11" cy="11" r="6.5" />
    <path d="M16 16l5 5" />
  </svg>
);

const IconBell = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M6 17h12l-1-7a5 5 0 10-10 0l-1 7z" />
    <path d="M10 17a2 2 0 004 0" />
  </svg>
);

const IconHelp = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9a2.5 2.5 0 114 2c0 1.5-2 1.5-2 3" />
    <circle cx="12" cy="17" r=".5" />
  </svg>
);

const IconAlert = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M12 4l9 16H3L12 4z" />
    <path d="M12 10v5" />
    <circle cx="12" cy="17.5" r=".6" />
  </svg>
);

export default MerchantEmptyState;
